import { useState, useEffect, useContext, useRef } from "react";

// Images for Vision Symptoms
import doubleVision from "../../images/double-vision.jpg";
import wordsMoving from "../../images/words-moving.jpg";

import { PreferredLanguageContext } from "../../lib/main-context";
import {
  HEADACHE_LOCATION,
  SYMPTOM_LIST,
} from "../../constants/OnboardingTranslation";

const CategorySelector = ({
  categoryType,
  categorizedItemList,
  setCategorizedItemList,
  colors,
  currentCategoryIndex,
  selectedItems,
  setSelectedItems,
  headacheParts,
  setHeadacheParts,
  setError,
}) => {
  const currentCategory = categorizedItemList[currentCategoryIndex].category;
  const currentCategoryItems =
    categorizedItemList[currentCategoryIndex].items || [];
  const [other, setOther] = useState("");

  const { language } = useContext(PreferredLanguageContext);
  const t = categoryType === "Symptoms" ? SYMPTOM_LIST[language] : null;

  // Logic specifically for Vision Symptoms
  const hoverImagePaths = {
    "Double vision": doubleVision,
    "Words move when reading": wordsMoving,
  };

  const headacheLabel =
    "Headaches caused by close work: computers, reading, gaming";
  const headacheOptions = [
    { label: HEADACHE_LOCATION[language].fore, value: "Fore" },
    { label: HEADACHE_LOCATION[language].backtop, value: "BackTop" },
    { label: HEADACHE_LOCATION[language].right, value: "Right" },
    { label: HEADACHE_LOCATION[language].left, value: "Left" },
    { label: HEADACHE_LOCATION[language].behind, value: "Behind" },
    { label: HEADACHE_LOCATION[language].eyes, value: "Eyes" },
    { label: HEADACHE_LOCATION[language].backbottom, value: "BackBottom" },
    { label: HEADACHE_LOCATION[language].all, value: "All" },
  ];
  const toggleHeadacheParts = (value) => {
    setHeadacheParts((prev) => {
      return prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
    });
  };
  // end vision symptom logic

  const handleToggle = (category, item) => {
    if (item === headacheLabel && selectedItems[category]?.includes(item)) {
      setHeadacheParts([]);
    }
    setSelectedItems((prev) => {
      const copy = { ...prev };
      const currentList = copy[category] ?? [];
      copy[category] = currentList.includes(item)
        ? currentList.filter((i) => i !== item)
        : [...currentList, item];

      return copy;
    });
  };

  const handleAddOther = () => {
    const item = other.trim();
    if (!item) return;

    const alreadyExists = categorizedItemList
      .find((group) => group.category === currentCategory)
      ?.items.some((existing) => existing.toLowerCase() === item.toLowerCase());

    if (alreadyExists) {
      setError("Item already exists in the list.");
      return;
    }

    setCategorizedItemList((prev) =>
      prev.map((group) =>
        group.category === currentCategory
          ? { ...group, items: [...group.items, item] }
          : group
      )
    );
    handleToggle(currentCategory, item);
    setOther("");
  };

  // Scrolls availableItems container to the top on category change
  // Keeps selectedItems container scrolled to the bottom on item addition
  const availableRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    setOther("");
    if (availableRef.current) {
      availableRef.current.scrollTop = 0;
    }
  }, [currentCategoryIndex]);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollTop = selectedRef.current.scrollHeight;
    }
  }, [selectedItems]);

  return (
    <div className="w-full">
      {/* category tabs */}
      <div className="flex">
        {categorizedItemList.map(({ category }) => (
          <div
            key={category}
            className="flex-1 mx-auto py-3 text-center first:rounded-tl-lg last:rounded-tr-lg"
            style={{ backgroundColor: colors[category] }}
          >
            <h6
              className="text-xs md:text-sm uppercase m-0"
              style={{
                color: currentCategory === category ? "white" : "black",
              }}
            >
              {category}
            </h6>
          </div>
        ))}
      </div>

      {/* selectable items container */}
      <div className="flex flex-col md:flex-row md:h-96">
        <div
          className="border-[1px] border-[var(--primary)] md:rounded-bl-lg bg-white px-10 py-4 w-full md:w-1/2 overflow-y-auto"
          ref={availableRef}
        >
          <div className="flex flex-col">
            <h6 className="capitalize">
              {currentCategory} {categoryType}:
            </h6>
            <p className="italic text-sm">(Choose all that apply)</p>

            {currentCategoryItems.map((item) => {
              const imagePath = hoverImagePaths[item];

              return (
                <label
                  className="group"
                  key={item}
                  style={{ "--checkbox-color": colors[currentCategory] }}
                >
                  <div className="relative flex items-start mb-2 space-x-2 cursor-pointer leading-tight">
                    <input
                      type="checkbox"
                      className="hidden peer"
                      checked={
                        selectedItems[currentCategory]?.includes(item) ?? false
                      }
                      onChange={() => handleToggle(currentCategory, item)}
                    />
                    <div
                      className="w-4 h-4 mt-[0.2rem] shrink-0 rounded-full border-2 border-current peer-checked:bg-current"
                      style={{ color: "var(--checkbox-color)" }}
                    />
                    <span>{t[currentCategory]?.[item] || item}</span>

                    {/* shows vision images on hover */}
                    {imagePath && (
                      <img
                        src={imagePath}
                        alt={item}
                        className="pointer-events-none z-10 absolute top-0 left-10 w-60 h-60 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    )}
                  </div>

                  {/* vision headacheParts sub-options */}
                  {item === headacheLabel &&
                    selectedItems[currentCategory]?.includes(headacheLabel) && (
                      <div className="mt-3 ml-2 text-sm">
                        <h6>{HEADACHE_LOCATION[language].label}</h6>
                        {headacheOptions.map(({ label, value }) => (
                          <label
                            key={value}
                            className="flex items-center space-x-2"
                          >
                            <input
                              type="checkbox"
                              onChange={() => toggleHeadacheParts(value)}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                </label>
              );
            })}

            <div className="flex items-center gap-2.5">
              <h6 className="font-bold mt-2 whitespace-nowrap">Other:</h6>
              <input
                className="border-b border-black py-0.5 focus:outline-none focus:ring-0"
                type="text"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddOther();
                }}
              />
            </div>
          </div>
        </div>

        {/* selectedItems container */}
        <div
          className="border-[1px] border-[var(--primary)] rounded-b-lg md:rounded-br-lg bg-white px-10 py-4 w-full md:w-1/2 min-h-96 overflow-y-auto"
          ref={selectedRef}
        >
          {Object.entries(selectedItems).flatMap(([category, items]) =>
            items.map((item) => (
              <div key={item} style={{ "--checkbox-color": colors[category] }}>
                <div className="flex items-start mb-2">
                  <div
                    className="w-4 h-4 mt-[0.2rem] shrink-0 rounded-full border-2 border-current bg-current"
                    style={{ color: "var(--checkbox-color)" }}
                  />
                  <span className="ml-2">{t[category]?.[item] || item}</span>
                </div>
                {item === headacheLabel && headacheParts.length > 0 && (
                  <ul className="flex flex-col list-disc text-sm ml-10 mt-2">
                    {headacheParts.map((part) => (
                      <li key={part}>
                        {HEADACHE_LOCATION[language][part.toLowerCase()]}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CategorySelector;
